import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import { socialApi } from "@bw/shared/api";
import type { MessagePage, MessageResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";
import {
  combineReadAuthority,
  exactReadAuthority,
  hasNetworkReadAuthority,
  networkReadAuthority,
  retainStillEligibleAuthority,
  withoutNetworkAuthority,
  type MessageHistoryData,
} from "./messageReadAuthority";

export const MESSAGE_PAGE_SIZE = 50;

type TombstoneAuthority = Map<
  string,
  { message: MessageResponse; readAuthorized: boolean }
>;

type PageCandidate = {
  page: MessagePage;
  pageParam: unknown;
};

export function confirmedTombstones(
  current: InfiniteData<MessagePage> | undefined,
): TombstoneAuthority {
  const tombstones: TombstoneAuthority = new Map();
  for (const page of current?.pages ?? []) {
    const readAuthorized = new Set(page.read_message_ids);
    for (const message of page.items) {
      if (!message.is_deleted) continue;
      const existing = tombstones.get(message.message_id);
      tombstones.set(message.message_id, {
        message,
        readAuthorized:
          Boolean(existing?.readAuthorized) ||
          readAuthorized.has(message.message_id),
      });
    }
  }
  return tombstones;
}

export function preserveTombstonesInPage(
  page: MessagePage,
  tombstones: ReadonlyMap<
    string,
    { message: MessageResponse; readAuthorized: boolean }
  >,
): MessagePage {
  if (tombstones.size === 0) return page;
  const readMessageIds = new Set(page.read_message_ids);
  return {
    ...page,
    items: page.items.map((message) => {
      const tombstone = tombstones.get(message.message_id);
      if (!tombstone || message.is_deleted) return message;
      if (tombstone.readAuthorized) readMessageIds.add(message.message_id);
      else readMessageIds.delete(message.message_id);
      return tombstone.message;
    }),
    read_message_ids: page.items
      .map((message) => message.message_id)
      .filter((messageId) => readMessageIds.has(messageId)),
  };
}

function normalizeHistory(
  candidates: PageCandidate[],
  tombstones: TombstoneAuthority,
  readAuthorityBatches: string[][],
): MessageHistoryData {
  const first = candidates[0];
  if (!first) {
    return { pages: [], pageParams: [], readAuthorityBatches };
  }

  const seen = new Set<string>();
  const normalized: PageCandidate[] = [];
  for (const candidate of candidates) {
    const protectedPage = preserveTombstonesInPage(candidate.page, tombstones);
    const items = protectedPage.items.filter((message) => {
      if (seen.has(message.message_id)) return false;
      seen.add(message.message_id);
      return true;
    });
    if (items.length === 0) continue;
    const itemIds = new Set(items.map((message) => message.message_id));
    normalized.push({
      page: {
        ...withoutNetworkAuthority(protectedPage),
        items,
        // The first occurrence is authoritative. A stale cached page can retain
        // read authority only for rows absent from the newer server page; it can
        // never re-add an overlapping ID that the server removed.
        read_message_ids: protectedPage.read_message_ids.filter((messageId) =>
          itemIds.has(messageId),
        ),
      },
      pageParam: candidate.pageParam,
    });
  }

  const head = normalized.shift();
  if (!head) return { pages: [], pageParams: [], readAuthorityBatches };
  const pages = [head.page];
  const pageParams: unknown[] = [null];
  let retainedItems: MessageResponse[] = [];
  let retainedReadIds = new Set<string>();
  let retainedParam: unknown = null;
  let retainedLastPage: MessagePage | undefined;

  const flushRetained = () => {
    if (retainedItems.length === 0 || !retainedLastPage) return;
    const itemIds = new Set(retainedItems.map((message) => message.message_id));
    pages.push({
      ...withoutNetworkAuthority(retainedLastPage),
      items: retainedItems,
      read_message_ids: retainedItems
        .map((message) => message.message_id)
        .filter(
          (messageId) =>
            itemIds.has(messageId) && retainedReadIds.has(messageId),
        ),
    });
    pageParams.push(retainedParam);
    retainedItems = [];
    retainedReadIds = new Set();
    retainedParam = null;
    retainedLastPage = undefined;
  };

  for (const candidate of normalized) {
    const candidateReadIds = new Set(candidate.page.read_message_ids);
    for (const message of candidate.page.items) {
      if (retainedItems.length === 0) {
        retainedParam =
          candidate.pageParam ?? pages[pages.length - 1]?.next_cursor ?? null;
      }
      retainedItems.push(message);
      if (candidateReadIds.has(message.message_id)) {
        retainedReadIds.add(message.message_id);
      }
      retainedLastPage = candidate.page;
      if (retainedItems.length === MESSAGE_PAGE_SIZE) flushRetained();
    }
  }
  flushRetained();
  return { pages, pageParams, readAuthorityBatches };
}

export function mergeHeadAuthority(
  current: InfiniteData<MessagePage>,
  authoritativeHead: MessagePage,
): InfiniteData<MessagePage> {
  const protectedHead = preserveTombstonesInPage(
    authoritativeHead,
    confirmedTombstones(current),
  );
  const currentHeadIds = new Set(
    current.pages[0]?.items.map((message) => message.message_id) ?? [],
  );
  const provesContinuity = protectedHead.items.some((message) =>
    currentHeadIds.has(message.message_id),
  );
  if (
    currentHeadIds.size > 0 &&
    (protectedHead.items.length === 0 || !provesContinuity)
  ) {
    return normalizeHistory(
      [{ page: protectedHead, pageParam: null }],
      confirmedTombstones(current),
      protectedHead.read_message_ids.length > 0
        ? [protectedHead.read_message_ids]
        : [],
    );
  }
  return normalizeHistory(
    [
      { page: protectedHead, pageParam: null },
      ...current.pages.map((page, index) => ({
        page,
        pageParam: current.pageParams[index],
      })),
    ],
    confirmedTombstones(current),
    combineReadAuthority(
      [protectedHead.read_message_ids],
      retainStillEligibleAuthority(exactReadAuthority(current), [
        protectedHead,
      ]),
    ),
  );
}

export function replaceLatestAuthority(
  current: InfiniteData<MessagePage>,
  authoritativeHead: MessagePage,
): MessageHistoryData {
  const head = preserveTombstonesInPage(
    authoritativeHead,
    confirmedTombstones(current),
  );
  return {
    pages: [withoutNetworkAuthority(head)],
    pageParams: [null],
    readAuthorityBatches:
      head.read_message_ids.length > 0 ? [[...head.read_message_ids]] : [],
  };
}

export function rebuildMessageHistory(
  current: InfiniteData<MessagePage>,
  candidates: PageCandidate[],
  readAuthorityBatches: string[][],
): MessageHistoryData {
  return normalizeHistory(
    candidates,
    confirmedTombstones(current),
    readAuthorityBatches,
  );
}

function preserveConfirmedTombstones(
  current: InfiniteData<MessagePage> | undefined,
  incoming: InfiniteData<MessagePage>,
): InfiniteData<MessagePage> {
  const tombstones = confirmedTombstones(current);
  if (tombstones.size === 0) return incoming;
  return {
    ...incoming,
    pages: incoming.pages.map((page) =>
      preserveTombstonesInPage(page, tombstones),
    ),
  };
}

function currentHeadIsNewer(
  current: MessagePage,
  incoming: MessagePage,
): boolean {
  const currentFirst = current.items[0];
  const incomingFirst = incoming.items[0];
  if (!currentFirst) return false;
  if (!incomingFirst) return true;
  const currentCreatedAt = Date.parse(currentFirst.created_at);
  const incomingCreatedAt = Date.parse(incomingFirst.created_at);
  if (currentCreatedAt !== incomingCreatedAt) {
    return currentCreatedAt > incomingCreatedAt;
  }
  if (currentFirst.message_id !== incomingFirst.message_id) {
    return currentFirst.message_id > incomingFirst.message_id;
  }
  return false;
}

export function preserveCurrentHeadOnPageAppend(
  current: InfiniteData<MessagePage> | undefined,
  incoming: InfiniteData<MessagePage>,
): InfiniteData<MessagePage> {
  const protectedIncoming = preserveConfirmedTombstones(current, incoming);
  const receivedPages = protectedIncoming.pages
    .map((page, index) => ({
      page,
      pageParam: protectedIncoming.pageParams[index],
      authority: networkReadAuthority(page),
    }))
    .filter((candidate) => hasNetworkReadAuthority(candidate.page));
  const receivedAuthority = receivedPages.flatMap(
    (candidate) => candidate.authority,
  );
  if (!current?.pages[0]) {
    return normalizeHistory(
      protectedIncoming.pages.map((page, index) => ({
        page,
        pageParam: protectedIncoming.pageParams[index],
      })),
      new Map(),
      receivedPages.length > 0
        ? receivedAuthority
        : protectedIncoming.pages.map((page) => page.read_message_ids),
    );
  }
  const pages = [...protectedIncoming.pages];
  const appendsPage = protectedIncoming.pages.length > current.pages.length;
  const hasExplicitAuthority = Boolean(
    (protectedIncoming as MessageHistoryData).readAuthorityBatches,
  );
  const appendsServerPage = receivedPages.some(
    (candidate) => candidate.pageParam !== null,
  );
  const sameHeadMembership =
    JSON.stringify(
      current.pages[0].items.map((message) => message.message_id),
    ) ===
    JSON.stringify(
      protectedIncoming.pages[0].items.map((message) => message.message_id),
    );
  const keepCurrentHead =
    currentHeadIsNewer(current.pages[0], protectedIncoming.pages[0]) ||
    (appendsPage && sameHeadMembership);
  if (keepCurrentHead) {
    pages[0] = current.pages[0];
  }
  const candidates =
    appendsServerPage && !hasExplicitAuthority
      ? [
          ...current.pages.map((page, index) => ({
            page,
            pageParam: current.pageParams[index],
          })),
          ...receivedPages.map(({ page, pageParam }) => ({ page, pageParam })),
        ]
      : pages.map((page, index) => ({
          page,
          pageParam: protectedIncoming.pageParams[index],
        }));
  return normalizeHistory(
    candidates,
    confirmedTombstones(current),
    hasExplicitAuthority
      ? exactReadAuthority(protectedIncoming)
      : combineReadAuthority(
          retainStillEligibleAuthority(
            exactReadAuthority(current),
            receivedPages.map((candidate) => candidate.page),
          ),
          receivedAuthority,
        ),
  );
}

export function pageAuthoritySignature(page: MessagePage): string {
  return JSON.stringify(page);
}

function updateConfirmedMessage(
  current: InfiniteData<MessagePage> | undefined,
  response: MessageResponse,
  latestOnly: boolean,
): InfiniteData<MessagePage> | undefined {
  if (!current?.pages[0]) {
    const head: MessagePage = {
      items: [response],
      next_cursor: null,
      has_more: false,
      read_message_ids: response.is_own ? [] : [response.message_id],
    };
    return {
      pages: [head],
      pageParams: [null],
      ...(latestOnly ? {} : { readAuthorityBatches: [] }),
    } as MessageHistoryData;
  }
  const tombstones = confirmedTombstones(current);
  const protectedResponse =
    tombstones.get(response.message_id)?.message ?? response;
  const priorHead = current.pages[0];
  const items = [
    protectedResponse,
    ...priorHead.items.filter(
      (message) => message.message_id !== response.message_id,
    ),
  ].slice(0, MESSAGE_PAGE_SIZE);
  const itemIds = new Set(items.map((message) => message.message_id));
  const head = {
    ...priorHead,
    items,
    read_message_ids: priorHead.read_message_ids.filter((messageId) =>
      itemIds.has(messageId),
    ),
  };
  return latestOnly
    ? { pages: [head], pageParams: [null] }
    : normalizeHistory(
        [
          { page: head, pageParam: null },
          ...current.pages.map((page, index) => ({
            page,
            pageParam: current.pageParams[index],
          })),
        ],
        confirmedTombstones(current),
        exactReadAuthority(current),
      );
}

export function installConfirmedMessage(
  queryClient: QueryClient,
  conversationId: string,
  response: MessageResponse,
): void {
  queryClient.setQueryData<InfiniteData<MessagePage>>(
    queryKeys.social.messages(conversationId),
    (current) => updateConfirmedMessage(current, response, false),
  );
  queryClient.setQueryData<InfiniteData<MessagePage>>(
    queryKeys.social.latestMessages(conversationId),
    (current) => updateConfirmedMessage(current, response, true),
  );
}

export function installConfirmedTombstone(
  queryClient: QueryClient,
  conversationId: string,
  messageId: string,
): void {
  for (const queryKey of [
    queryKeys.social.messages(conversationId),
    queryKeys.social.latestMessages(conversationId),
  ]) {
    queryClient.setQueryData<InfiniteData<MessagePage>>(queryKey, (current) =>
      current
        ? {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              items: page.items.map((message) =>
                message.message_id === messageId
                  ? {
                      ...message,
                      content: null,
                      is_deleted: true,
                      sender_avatar_url: null,
                      seen_by_others: false,
                      shared_post: null,
                      shared_post_id: null,
                      shared_post_unavailable: false,
                    }
                  : message,
              ),
            })),
          }
        : current,
    );
  }
}

/** Reconcile the full-thread cache with one head-only server read. */
export async function reconcileMessagePages(
  queryClient: QueryClient,
  conversationId: string,
  signal?: AbortSignal,
): Promise<void> {
  const queryKey = queryKeys.social.messages(conversationId);
  const before = queryClient.getQueryData<InfiniteData<MessagePage>>(queryKey);
  if (!before?.pages[0]) return;
  const beforeHeadSignature = pageAuthoritySignature(before.pages[0]);

  try {
    const head = await socialApi.getMessages(conversationId, {
      limit: MESSAGE_PAGE_SIZE,
      ...(signal ? { signal } : {}),
    });
    if (signal?.aborted) return;
    queryClient.setQueryData<InfiniteData<MessagePage>>(queryKey, (current) => {
      if (
        !current?.pages[0] ||
        pageAuthoritySignature(current.pages[0]) !== beforeHeadSignature
      ) {
        return current;
      }
      return mergeHeadAuthority(current, head);
    });
  } catch {
    // Confirmed cache truth remains readable; the next bounded poll retries.
  }
}
